import { memo, useMemo } from 'react';
import type { SkillBranch, Vector2 } from '@sorrem/shared';
import type { SkillTreeModel } from './skill-tree-model';
import { SKILL_TREE_BOUNDS } from './skill-tree-layout';
import type { SkillTreeSvgIds } from './skill-tree-ids';
import type { SkillTreeIntroSchedule, SkillTreeIntroWindow } from './skill-tree-intro';
import {
    buildSkillTreeLinks,
    resolveCraftingPath,
    resolveEssencePath,
    resolveLinkCenterline,
    resolveMartialRoute,
    type LinkSpec,
} from './skill-tree-link-geometry';
import {
    GOLD_FACE,
    GOLD_HIGHLIGHT,
    GOLD_RIDGE,
    GOLD_SHADOW,
    IRON,
    IRON_DARK,
    IRON_HIGHLIGHT,
    VIOLET,
    VIOLET_PALE,
    WHITE,
} from './skill-tree-palette';
import { useSkillTreeIntroPart } from './SkillTreeIntroPart';
import styles from './SkillTreeScene.module.scss';

interface SkillTreeLinksProps {
    readonly model: SkillTreeModel;
    /** The Skills tab intro's schedule while it plays: each link grows out of its parent, or retracts into it. */
    readonly intro: SkillTreeIntroSchedule | undefined;
    /** The scene's SVG def ids (the mist mask, each link's reveal mask). */
    readonly svgIds: SkillTreeSvgIds;
}

/** The game's essence-stream mist (`game-content/assets/vfx/essence-stream`), used as the Essence links' alpha. */
const MIST_CLUMPS_URL = '/assets/vfx/essence-stream/SoulMist_02.png';
const MIST_CLOUD_URL = '/assets/vfx/essence-stream/SoulMist_01.png';
/** Tile sizes, in tree units, of the two mist layers. */
const MIST_CLUMPS_TILE = 220;
const MIST_CLOUD_TILE = 300;

/** Chain link pitch along the path, and each link's size, in tree units. */
const CHAIN_PITCH = 13;
const CHAIN_LINK_LENGTH = 18;

function pointsToPath(points: readonly Vector2[]): string {
    return points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join('');
}

/** One winding strand of an Essence wisp. Each covers only part of the link and wanders on its own mix of two waves, so strands drift apart and rejoin rather than braiding. */
interface WispStrand {
    /** The stretch of the link it covers, 0 to 1. */
    readonly from: number;
    readonly to: number;
    /** Sideways reach, relative to the link's spread. */
    readonly amplitude: number;
    /** Waves along the whole link, for its slow and fast components. */
    readonly slow: number;
    readonly fast: number;
    readonly color: string;
    readonly width: number;
    readonly opacity: number;
}

const WISP_STRANDS: readonly WispStrand[] = [
    { from: 0, to: 1, amplitude: 0.35, slow: 0.7, fast: 2.3, color: WHITE, width: 1.6, opacity: 0.9 },
    { from: 0.08, to: 0.92, amplitude: 1, slow: 1.1, fast: 3.1, color: VIOLET_PALE, width: 1.4, opacity: 0.85 },
    { from: 0.22, to: 1, amplitude: 0.8, slow: 0.9, fast: 2.6, color: WHITE, width: 1, opacity: 0.7 },
    { from: 0, to: 0.7, amplitude: 0.7, slow: 1.6, fast: 0.5, color: VIOLET, width: 1.2, opacity: 0.85 },
];

/**
 * A strand's points: the stretch of `points` it covers, pushed sideways by
 * its waves and tapered to nothing at both of its ends. `seed` shifts the
 * phases so neighboring links don't wind alike.
 */
function wispStrandPoints(points: readonly Vector2[], strand: WispStrand, spread: number, waves: number, seed: number): Vector2[] {
    const last = points.length - 1;
    const first = Math.round(strand.from * last);
    const final = Math.round(strand.to * last);
    const slowPhase = seed * 2.39;
    const fastPhase = seed * 1.71 + strand.amplitude * 5;
    return points.slice(first, final + 1).map((point, offsetIndex) => {
        const index = first + offsetIndex;
        const previous = points[Math.max(0, index - 1)]!;
        const next = points[Math.min(last, index + 1)]!;
        const tangentLength = Math.hypot(next.x - previous.x, next.y - previous.y) || 1;
        const normal = { x: -(next.y - previous.y) / tangentLength, y: (next.x - previous.x) / tangentLength };
        const t = index / last;
        const local = final > first ? offsetIndex / (final - first) : 0;
        const wave = 0.65 * Math.sin(2 * Math.PI * strand.slow * waves * t + slowPhase) + 0.35 * Math.sin(2 * Math.PI * strand.fast * waves * t + fastPhase);
        const offset = spread * strand.amplitude * Math.sin(Math.PI * local) * wave;
        return { x: point.x + normal.x * offset, y: point.y + normal.y * offset };
    });
}

/** Martial: a beveled gold bar on straight and 45 degrees runs, with a bright center ridge and beveled corners. */
function MartialLink({ link }: { readonly link: LinkSpec }): JSX.Element {
    const route = resolveMartialRoute(link);
    const d = pointsToPath(route);
    const width = 5 + 5 * link.strength;
    return (
        <g strokeLinejoin="bevel" strokeLinecap="butt" opacity={0.55 + 0.4 * link.strength} strokeDasharray={link.isInactive ? `${width * 2.4} ${width * 1.6}` : undefined}>
            <path d={d} stroke={GOLD_SHADOW} strokeWidth={width + 3} strokeOpacity={0.85} />
            <path d={d} stroke={GOLD_FACE} strokeWidth={width} />
            <path d={d} stroke={GOLD_RIDGE} strokeWidth={width * 0.45} />
            <path d={d} stroke={GOLD_HIGHLIGHT} strokeWidth={Math.max(0.8, width * 0.12)} strokeOpacity={0.75} />
        </g>
    );
}

/** Essence glow: a soft violet band, drawn inside the group that blurs every Essence glow at once. */
function EssenceGlow({ link }: { readonly link: LinkSpec }): JSX.Element {
    const { points } = resolveEssencePath(link);
    const width = 3 + 6 * link.strength;
    return <path d={pointsToPath(points)} stroke={VIOLET} strokeWidth={width * 2.6} strokeOpacity={0.45 + 0.3 * link.strength} strokeLinecap="round" />;
}

/** Essence strands: fine white and violet threads wandering along the curve, broken into wisps by the mist mask. */
function EssenceStrands({ link }: { readonly link: LinkSpec }): JSX.Element {
    const { points, length } = resolveEssencePath(link);
    // About one slow wave per 120 units, so short and long links wander alike.
    const waves = Math.max(1, length / 120);
    const spread = 4 + 6 * link.strength;
    return (
        <g strokeLinecap="round" strokeLinejoin="round" opacity={0.75 + 0.25 * link.strength}>
            {WISP_STRANDS.map((strand, index) => (
                <path
                    key={index}
                    d={pointsToPath(wispStrandPoints(points, strand, spread, waves, link.index + 1))}
                    stroke={strand.color}
                    strokeWidth={strand.width}
                    strokeOpacity={strand.opacity}
                />
            ))}
        </g>
    );
}

/** Crafting: a chain of iron links, alternating face-on and edge-on, slack along a gentle curve. */
function CraftingLink({ link }: { readonly link: LinkSpec }): JSX.Element {
    const points = resolveCraftingPath(link);
    // Distance along the curve, to place links evenly.
    const distances = points.reduce<number[]>((all, point, index) => {
        all.push(index === 0 ? 0 : all[index - 1]! + Math.hypot(point.x - points[index - 1]!.x, point.y - points[index - 1]!.y));
        return all;
    }, []);
    const total = distances[distances.length - 1] ?? 0;
    const count = Math.max(1, Math.floor(total / CHAIN_PITCH));
    const pitch = total / count;
    const chainLinks = Array.from({ length: count }, (_, index) => {
        const distance = pitch * (index + 0.5);
        let segment = 1;
        while (segment < distances.length - 1 && distances[segment]! < distance) segment += 1;
        const start = points[segment - 1]!;
        const end = points[segment]!;
        const span = distances[segment]! - distances[segment - 1]! || 1;
        const t = (distance - distances[segment - 1]!) / span;
        return {
            x: start.x + (end.x - start.x) * t,
            y: start.y + (end.y - start.y) * t,
            angle: (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI,
            faceOn: index % 2 === 0,
        };
    });
    const half = CHAIN_LINK_LENGTH / 2;
    return (
        <g className={link.isFaint ? styles.chainFaint : undefined}>
            {chainLinks.map((chainLink, index) => (
                <g key={index} transform={`translate(${chainLink.x.toFixed(1)} ${chainLink.y.toFixed(1)}) rotate(${chainLink.angle.toFixed(1)})`}>
                    {chainLink.faceOn ? (
                        <>
                            <ellipse rx={half} ry={5.5} stroke={IRON_DARK} strokeWidth={4.6} />
                            <ellipse rx={half} ry={5.5} stroke={IRON} strokeWidth={2.8} />
                            <path d={`M${-half * 0.6} -4.4Q0 -6.2 ${half * 0.6} -4.4`} stroke={IRON_HIGHLIGHT} strokeWidth={1} strokeLinecap="round" />
                        </>
                    ) : (
                        <>
                            <rect x={-half} y={-2.3} width={CHAIN_LINK_LENGTH} height={4.6} rx={2.3} fill={IRON_DARK} />
                            <rect x={-half + 1} y={-1.4} width={CHAIN_LINK_LENGTH - 2} height={2.8} rx={1.4} fill={IRON} />
                            <line x1={-half + 3} y1={-0.6} x2={half - 3} y2={-0.6} stroke={IRON_HIGHLIGHT} strokeWidth={0.8} strokeLinecap="round" />
                        </>
                    )}
                </g>
            ))}
        </g>
    );
}

/** Width of each branch's intro reveal, wide enough to cover the link's glow, strands, or chain. */
const REVEAL_WIDTH: Record<SkillBranch, number> = { martial: 30, essence: 100, crafting: 32 };

/** A mask that draws one link out from its parent along its own line as the intro's clock passes its window (or back into it). */
function LinkRevealMask({ id, link, window }: { readonly id: string; readonly link: LinkSpec; readonly window: SkillTreeIntroWindow }): JSX.Element {
    const ref = useSkillTreeIntroPart(window, 'sweep');
    const { min, max } = SKILL_TREE_BOUNDS;
    return (
        <mask id={id} maskUnits="userSpaceOnUse" x={min.x} y={min.y} width={max.x - min.x} height={max.y - min.y}>
            <path
                ref={ref}
                className={styles.introSweep}
                d={pointsToPath(resolveLinkCenterline(link))}
                stroke="#fff"
                strokeWidth={REVEAL_WIDTH[link.branch]}
                strokeLinecap="butt"
                strokeLinejoin="round"
                pathLength={100}
            />
        </mask>
    );
}

function LinkRevealMasks({ links, intro, svgIds }: {
    readonly links: readonly LinkSpec[];
    readonly intro: SkillTreeIntroSchedule;
    readonly svgIds: SkillTreeSvgIds;
}): JSX.Element {
    return (
        <defs>
            {links.map((link) => {
                const window = intro.links.get(link.key);
                return window ? <LinkRevealMask key={link.key} id={svgIds.linkReveal(link.key)} link={link} window={window} /> : null;
            })}
        </defs>
    );
}

/**
 * The mist mask over every Essence link: the game's two essence-stream mist
 * textures, tiled at different scales, drive the links' alpha so they break
 * into wisps, over a faint floor so a link never vanishes.
 */
function MistMask({ id }: { readonly id: string }): JSX.Element {
    const { min, max } = SKILL_TREE_BOUNDS;
    const area = { x: min.x, y: min.y, width: max.x - min.x, height: max.y - min.y };
    return (
        <defs>
            <pattern id={`${id}-clumps`} patternUnits="userSpaceOnUse" width={MIST_CLUMPS_TILE} height={MIST_CLUMPS_TILE}>
                <image href={MIST_CLUMPS_URL} width={MIST_CLUMPS_TILE} height={MIST_CLUMPS_TILE} />
            </pattern>
            <pattern
                id={`${id}-cloud`}
                patternUnits="userSpaceOnUse"
                width={MIST_CLOUD_TILE}
                height={MIST_CLOUD_TILE}
                patternTransform="rotate(23)"
            >
                <image href={MIST_CLOUD_URL} width={MIST_CLOUD_TILE} height={MIST_CLOUD_TILE} />
            </pattern>
            <filter id={`${id}-blur`} filterUnits="userSpaceOnUse" {...area}>
                <feGaussianBlur stdDeviation={6} />
            </filter>
            <mask id={id} maskUnits="userSpaceOnUse" {...area} style={{ maskType: 'alpha' }}>
                <rect {...area} fill="#fff" fillOpacity={0.35} />
                <rect {...area} fill={`url(#${id}-cloud)`} opacity={0.8} />
                <rect {...area} fill={`url(#${id}-clumps)`} />
            </mask>
        </defs>
    );
}

/**
 * The tree's connectors: sigil to each dial, each dial to its grouping
 * roots, and each root to its skills, in the branch's style. Martial runs
 * are beveled gold bars on straight and 45 degrees lines, Essence links are mist
 * wisps, and Crafting links are iron chains. Thickness and brightness
 * follow the level of the skill a link leads to. The recent-training pulse along them is drawn by the effects
 * layer (see `skill-tree-effects.ts`).
 */
export const SkillTreeLinks = memo(function SkillTreeLinks({ model, intro, svgIds }: SkillTreeLinksProps): JSX.Element {
    // The links follow the model alone; hovering or selecting a node doesn't move them.
    const links = useMemo(() => buildSkillTreeLinks(model), [model]);
    const essenceLinks = useMemo(() => links.filter((link) => link.branch === 'essence'), [links]);
    const reveal = (link: LinkSpec): string | undefined => (intro?.links.has(link.key) ? `url(#${svgIds.linkReveal(link.key)})` : undefined);

    return (
        <g className={styles.links}>
            <MistMask id={svgIds.mistMask} />
            {intro && <LinkRevealMasks links={links} intro={intro} svgIds={svgIds} />}
            <g mask={`url(#${svgIds.mistMask})`}>
                {/* One blur for every glow, so the bands have no edge. */}
                <g filter={`url(#${svgIds.mistMask}-blur)`}>
                    {essenceLinks.map((link) => (
                        <g key={link.key} mask={reveal(link)}>
                            <EssenceGlow link={link} />
                        </g>
                    ))}
                </g>
                {essenceLinks.map((link) => (
                    <g key={link.key} mask={reveal(link)}>
                        <EssenceStrands link={link} />
                    </g>
                ))}
            </g>
            {links.filter((link) => link.branch === 'martial').map((link) => (
                <g key={link.key} mask={reveal(link)}>
                    <MartialLink link={link} />
                </g>
            ))}
            {links.filter((link) => link.branch === 'crafting').map((link) => (
                <g key={link.key} mask={reveal(link)}>
                    <CraftingLink link={link} />
                </g>
            ))}
        </g>
    );
});
